import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '@/services/api-base';
import type { CatalogOverview } from '@/services/catalog-api';
import type { BookClubStats } from '@/services/bookclub-api';

/* Live counts beside the sign-in and sign-up forms. They go through
   publicApi, without the auth cookie: a revoked cookie answers 401 even on
   public endpoints, and the shared client turns any 401 into a redirect to
   /signin, which would loop on these very pages. The panel only shows on
   large screens, so nothing is fetched below that. A figure stays hidden
   until it loads, and book clubs only show while at least one is running. */
const LibraryFigures: React.FC = () => {
  const wide = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches;
  const { data: catalog } = useQuery({
    queryKey: ['public', 'catalog', 'overview'],
    queryFn: () => publicApi.get<CatalogOverview>('/catalog/overview/').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    enabled: !!wide,
  });
  const { data: clubStats } = useQuery({
    queryKey: ['public', 'bookclub', 'stats'],
    queryFn: () => publicApi.get<BookClubStats>('/bookclub/stats/').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    enabled: !!wide,
  });

  const figures = [
    { num: catalog?.counts?.books, label: 'Titles' },
    { num: catalog?.counts?.authors, label: 'Authors' },
    { num: clubStats?.active_clubs, label: 'Book clubs' },
  ].filter((f): f is { num: number; label: string } => typeof f.num === 'number' && f.num > 0);

  if (figures.length === 0) return null;

  return (
    <div
      className="hidden lg:grid mt-10 gap-px bg-[var(--border)] border border-[var(--border)] rounded-2xl overflow-hidden max-w-[520px]"
      style={{ gridTemplateColumns: `repeat(${figures.length}, minmax(0, 1fr))` }}
    >
      {figures.map((s) => (
        <div key={s.label} className="bg-[var(--surface)] p-5">
          <div className="font-display text-[1.5rem] font-semibold text-[var(--ink)] tracking-[-0.02em]">
            {s.num.toLocaleString()}
          </div>
          <div className="mt-1 text-[12px] text-[var(--muted)]">{s.label}</div>
        </div>
      ))}
    </div>
  );
};

export default LibraryFigures;
